import { useRef, useEffect, useState, useCallback, useMemo } from "react"
import { Stage, Layer, Rect, Text, Image as KonvaImage, Transformer } from "react-konva"
import type Konva from "konva"
import { useAuth } from "@clerk/react"
import { useCompositorStore } from "@/lib/stores/compositor-store"
import { usePipelineStore } from "@/lib/stores/pipeline-store"
import { createAssetsApi } from "@/lib/api/assets"
import type { CompositorLayer } from "@/lib/types/compositor"
import { loadFont } from "@/lib/registry/compositor-fonts"

const TEXT_MODULES = ["text_input", "llm_text", "text_builder"]

function isAssetRef(v: unknown): v is { assetId: string } {
  return typeof v === "object" && v !== null && "assetId" in v && typeof (v as { assetId: unknown }).assetId === "string"
}

function useAssetUrl(assetId: string | null | undefined): string | null {
  const { getToken } = useAuth()
  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken])
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!assetId) return
    let url: string | null = null
    assetsApi.fetchContent(assetId).then((blob) => {
      url = URL.createObjectURL(blob)
      setObjectUrl(url)
    }).catch(() => setObjectUrl(null))
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [assetId, assetsApi])

  return objectUrl
}

function hexToRgba(hex: string, opacity: number): string {
  const h = hex.padEnd(6, "f").slice(0, 6)
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${opacity / 100})`
}

function useKonvaImage(src: string | null): HTMLImageElement | null {
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  useEffect(() => {
    if (!src) return
    let active = true
    const img = new window.Image()
    img.crossOrigin = "anonymous"
    img.onload = () => { if (active) setImage(img) }
    img.src = src
    return () => { active = false }
  }, [src])
  return src ? image : null
}

interface LayerNodeProps {
  layer: CompositorLayer
  isSelected: boolean
  onSelect: () => void
  onTransformEnd: (layerId: string, patch: { x?: number; y?: number; width?: number; height?: number; rotation?: number }) => void
  nodeRef: (id: string, node: Konva.Node | null) => void
  imageSrc?: string
  assetId?: string
  textContent?: string
  onImageLoad?: (w: number, h: number) => void
}

function ImageLayerNode({ layer, isSelected, onSelect, onTransformEnd, nodeRef, imageSrc, assetId, onImageLoad }: LayerNodeProps) {
  const ref = useRef<Konva.Image>(null)
  const resolvedAssetUrl = useAssetUrl(assetId)
  const image = useKonvaImage(resolvedAssetUrl ?? imageSrc ?? null)

  useEffect(() => {
    nodeRef(layer.id, ref.current)
    return () => { nodeRef(layer.id, null) }
  }, [layer.id, nodeRef])

  useEffect(() => {
    if (image && onImageLoad) {
      onImageLoad(image.naturalWidth, image.naturalHeight)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image])

  const { x, y, width, height, rotation } = layer.transform
  const scaleX = layer.transform.flipH ? -1 : 1
  const scaleY = layer.transform.flipV ? -1 : 1

  return (
    <KonvaImage
      ref={ref}
      image={image ?? undefined}
      x={x}
      y={y}
      width={width}
      height={height}
      rotation={rotation}
      scaleX={scaleX}
      scaleY={scaleY}
      opacity={layer.opacity / 100}
      visible={layer.visible}
      draggable={!layer.locked && isSelected}
      onClick={onSelect}
      onTap={onSelect}
      onDragEnd={(e) => {
        onTransformEnd(layer.id, { x: Math.round(e.target.x()), y: Math.round(e.target.y()) })
      }}
      onTransformEnd={(e) => {
        const node = e.target
        onTransformEnd(layer.id, {
          x: Math.round(node.x()),
          y: Math.round(node.y()),
          width: Math.round(node.width() * node.scaleX()),
          height: Math.round(node.height() * node.scaleY()),
          rotation: node.rotation(),
        })
        node.scaleX(1)
        node.scaleY(1)
      }}
      fill={image ? undefined : "#444444"}
    />
  )
}

function TextLayerNode({ layer, isSelected, onSelect, onTransformEnd, nodeRef, textContent }: LayerNodeProps) {
  const ref = useRef<Konva.Text>(null)
  const style = layer.textStyle

  useEffect(() => {
    nodeRef(layer.id, ref.current)
    return () => { nodeRef(layer.id, null) }
  }, [layer.id, nodeRef])

  useEffect(() => {
    if (!style?.fontFamily) return
    const weightMap: Record<string, string> = {
      Thin: "100", ExtraLight: "200", Light: "300", Regular: "400",
      Medium: "500", "Semi Bold": "600", Bold: "700", "Extra Bold": "800",
    }
    const w = style.fontWeight ? (weightMap[style.fontWeight] ?? "400") : "400"
    loadFont(style.fontFamily, w).then(() => {
      ref.current?.getLayer()?.batchDraw()
    })
  }, [style?.fontFamily, style?.fontWeight])

  const { x, y, width, height, rotation } = layer.transform

  return (
    <Text
      ref={ref}
      x={x}
      y={y}
      width={width}
      height={height}
      rotation={rotation}
      opacity={layer.opacity / 100}
      visible={layer.visible}
      draggable={!layer.locked && isSelected}
      onClick={onSelect}
      onTap={onSelect}
      text={textContent ?? "Text Layer"}
      fontFamily={style?.fontFamily ?? "Inter"}
      fontSize={style?.fontSize ?? 48}
      fontStyle={style?.fontStyle?.toLowerCase() === "italic" ? "italic" : "normal"}
      fontVariant={style?.fontWeight?.toLowerCase() === "bold" ? "bold" : "normal"}
      align={style?.textAlign ?? "center"}
      verticalAlign={style?.verticalAlign === "center" ? "middle" : (style?.verticalAlign ?? "middle") as "top" | "middle" | "bottom"}
      fill={hexToRgba(style?.fill ?? "ffffff", style?.fillOpacity ?? 100)}
      letterSpacing={((style?.letterSpacing ?? 100) - 100) / 100 * (style?.fontSize ?? 48)}
      onDragEnd={(e) => {
        onTransformEnd(layer.id, { x: Math.round(e.target.x()), y: Math.round(e.target.y()) })
      }}
      onTransformEnd={(e) => {
        const node = e.target
        onTransformEnd(layer.id, {
          x: Math.round(node.x()),
          y: Math.round(node.y()),
          width: Math.round(node.width() * node.scaleX()),
          height: Math.round(node.height() * node.scaleY()),
          rotation: node.rotation(),
        })
        node.scaleX(1)
        node.scaleY(1)
      }}
    />
  )
}

interface LayerConnection {
  imageSrc?: string
  assetId?: string
  textContent?: string
  renderType: "image" | "text"
}

export function CompositorCanvas() {
  const config = useCompositorStore((s) => s.config)
  const nodeId = useCompositorStore((s) => s.nodeId)
  const selectedItem = useCompositorStore((s) => s.selectedItem)
  const selectItem = useCompositorStore((s) => s.selectItem)
  const updateTransform = useCompositorStore((s) => s.updateTransform)
  const updateCanvas = useCompositorStore((s) => s.updateCanvas)
  const zoom = useCompositorStore((s) => s.zoom)
  const requestExport = useCompositorStore((s) => s.requestExport)
  const requestSnapshot = useCompositorStore((s) => s.requestSnapshot)
  const finalizeClose = useCompositorStore((s) => s.finalizeClose)

  const pipelineNodes = usePipelineStore((s) => s.nodes)
  const pipelineEdges = usePipelineStore((s) => s.edges)

  const containerRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<Konva.Stage>(null)
  const transformerRef = useRef<Konva.Transformer>(null)
  const nodeRefs = useRef<Map<string, Konva.Node>>(new Map())
  const [containerSize, setContainerSize] = useState({ width: 800, height: 450 })

  // Derive what's actually connected to each layer
  const layerConnections = useMemo<Record<string, LayerConnection>>(() => {
    if (!nodeId) return {}
    const result: Record<string, LayerConnection> = {}
    const compositorEdges = pipelineEdges.filter((e) => e.target === nodeId)
    for (const edge of compositorEdges) {
      const sourceNode = pipelineNodes.find((n) => n.id === edge.source)
      if (!sourceNode || !edge.targetHandle) continue
      const layerId = edge.targetHandle
      const module = sourceNode.data.module
      const cfg = sourceNode.data.config

      if (TEXT_MODULES.includes(module)) {
        result[layerId] = {
          textContent: String(cfg.text ?? cfg.output ?? ""),
          renderType: "text",
        }
      } else {
        const imgCfg = cfg.image
        if (isAssetRef(imgCfg)) {
          result[layerId] = { assetId: imgCfg.assetId, renderType: "image" }
        } else {
          const imgConfig = imgCfg as { dataUrl?: string } | undefined
          result[layerId] = { imageSrc: imgConfig?.dataUrl, renderType: "image" }
        }
      }
    }
    return result
  }, [nodeId, pipelineNodes, pipelineEdges])

  // Responsive container sizing
  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  // Attach transformer to selected node
  useEffect(() => {
    if (!transformerRef.current) return
    if (selectedItem?.type === "layer" && selectedItem.layerId) {
      const node = nodeRefs.current.get(selectedItem.layerId)
      if (node) {
        transformerRef.current.nodes([node])
      } else {
        transformerRef.current.nodes([])
      }
    } else {
      transformerRef.current.nodes([])
    }
    transformerRef.current.getLayer()?.batchDraw()
  }, [selectedItem])

  const registerNodeRef = useCallback((id: string, node: Konva.Node | null) => {
    if (node) {
      nodeRefs.current.set(id, node)
    } else {
      nodeRefs.current.delete(id)
    }
  }, [])

  const handleTransformEnd = useCallback((layerId: string, patch: { x?: number; y?: number; width?: number; height?: number; rotation?: number }) => {
    updateTransform(layerId, patch)
  }, [updateTransform])

  // Export canvas as PNG when triggered
  useEffect(() => {
    if (!requestExport || !stageRef.current || !config) return
    const stage = stageRef.current
    const dataUrl = stage.toDataURL({ mimeType: "image/png", pixelRatio: 1 / scale })
    const link = document.createElement("a")
    link.download = `compositor-${config.canvas.width}x${config.canvas.height}.png`
    link.href = dataUrl
    link.click()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestExport])

  // Snapshot canvas for node preview when editor is about to close
  useEffect(() => {
    if (!requestSnapshot || !stageRef.current) return
    const stage = stageRef.current
    // Capture at ~480px wide for a compact preview thumbnail
    const pixelRatio = 480 / stage.width()
    const dataUrl = stage.toDataURL({ mimeType: "image/jpeg", quality: 0.85, pixelRatio })
    finalizeClose(dataUrl)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestSnapshot])

  // Auto-size the canvas once when the background image first loads
  const bgAutoSizedRef = useRef<string | null>(null)
  const handleBgImageLoad = useCallback((w: number, h: number) => {
    if (!config) return
    const key = `${w}x${h}`
    if (bgAutoSizedRef.current === key) return
    bgAutoSizedRef.current = key
    if (w !== config.canvas.width || h !== config.canvas.height) {
      updateCanvas({ width: w, height: h })
    }
  }, [config, updateCanvas])

  if (!config) return null

  const { width: canvasW, height: canvasH } = config.canvas

  // Scale canvas to fit container while maintaining aspect ratio
  const scaleX = containerSize.width / canvasW
  const scaleY = containerSize.height / canvasH
  const baseScale = Math.min(scaleX, scaleY) * 0.92
  const scale = baseScale * zoom

  const stageWidth = canvasW * scale
  const stageHeight = canvasH * scale

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center overflow-hidden"
      onClick={(e) => {
        if (e.target === containerRef.current) {
          selectItem({ type: "canvas" })
        }
      }}
    >
      <Stage
        ref={stageRef}
        width={stageWidth}
        height={stageHeight}
        scaleX={scale}
        scaleY={scale}
        style={{ cursor: "default" }}
      >
        {/* Background fill */}
        <Layer>
          <Rect x={0} y={0} width={canvasW} height={canvasH} fill="#1a1a1a" />
        </Layer>

        {/* Content layers in z-order */}
        <Layer>
          {config.layers.map((layer: CompositorLayer) => {
            const isSelected = selectedItem?.type === "layer" && selectedItem.layerId === layer.id
            const connection = layerConnections[layer.id]
            const layerProps: LayerNodeProps = {
              layer,
              isSelected,
              onSelect: () => selectItem({ type: "layer", layerId: layer.id }),
              onTransformEnd: handleTransformEnd,
              nodeRef: registerNodeRef,
              imageSrc: connection?.imageSrc,
              assetId: connection?.assetId,
              textContent: connection?.textContent,
            }

            if (layer.type === "background") {
              const bgConnection = layerConnections[layer.id]
              if (bgConnection?.renderType === "image" && (bgConnection.imageSrc || bgConnection.assetId)) {
                return <ImageLayerNode key={layer.id} {...layerProps} layer={{ ...layer, transform: { ...layer.transform, x: 0, y: 0, width: canvasW, height: canvasH } }} onImageLoad={handleBgImageLoad} />
              }
              return (
                <Rect
                  key={layer.id}
                  x={0}
                  y={0}
                  width={canvasW}
                  height={canvasH}
                  fill="#2a2a2a"
                  opacity={layer.opacity / 100}
                  visible={layer.visible}
                  onClick={() => selectItem({ type: "layer", layerId: layer.id })}
                  onTap={() => selectItem({ type: "layer", layerId: layer.id })}
                />
              )
            }

            // Use connection to determine render type; fall back to layer.type
            const renderType = connection?.renderType ?? (layer.type === "text" ? "text" : "image")

            if (renderType === "text") {
              return <TextLayerNode key={layer.id} {...layerProps} />
            }

            return <ImageLayerNode key={layer.id} {...layerProps} />
          })}
        </Layer>

        {/* UI layer — Transformer handles */}
        <Layer>
          <Transformer
            ref={transformerRef}
            borderStroke="#c8a84e"
            borderStrokeWidth={1}
            anchorSize={8}
            anchorFill="#c8a84e"
            anchorStroke="#c8a84e"
            rotateAnchorOffset={24}
          />
        </Layer>
      </Stage>
    </div>
  )
}
